/**
 * Shared search scoring (ADR-0004 text folding, ADR-0026 item scoring). Both
 * the server `/api/search` and the frontend local search rank items through
 * this single scorer, so a query ranks identically wherever it runs — the same
 * reason the `field:` grammar lives in `query.ts` (ADR-0022, E2).
 */
/**
 * Normalize a string for matching (ADR-0004): Unicode NFD, strip combining
 * marks (`häuser` → `hauser`), lowercase, fold `ß` → `ss` (matching the Orama
 * German stemmer, ADR-0036 decision 6), drop hyphens and whitespace.
 */
export function foldText(text) {
    return text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/ß/g, "ss")
        .replace(/[\s-]+/g, "");
}
/** Field tokens: split on whitespace and hyphens, then fold each (ADR-0004). */
export function tokenize(text) {
    return text.split(/[\s-]+/).filter(Boolean).map(foldText);
}
/**
 * Query terms are split on whitespace only (ADR-0004); hyphens fold away
 * inside a term, so `Winter-Jacke` and `Winterjacke` are one identical term.
 */
export function queryTerms(query) {
    return query.split(/\s+/).filter(Boolean).map(foldText);
}
/** The item score table: match kind × field → points. */
const ITEM_NAME_EXACT = 100;
const ITEM_NAME_PREFIX = 80;
const ITEM_NAME_CONTAINS = 50;
const ITEM_BARCODE = 60;
const ITEM_CATEGORY_EXACT = 70;
const ITEM_CATEGORY_PREFIX = 60;
const ITEM_METADATA = 40;
/** Best score of one term against an item's fields (0 when none match). */
function itemTermScore(item, term) {
    let best = 0;
    const nameFold = foldText(item.name);
    if (nameFold === term)
        best = ITEM_NAME_EXACT;
    else if (nameFold.startsWith(term))
        best = ITEM_NAME_PREFIX;
    else if (nameFold.includes(term))
        best = ITEM_NAME_CONTAINS;
    if (item.barcode && foldText(item.barcode).includes(term))
        best = Math.max(best, ITEM_BARCODE);
    const catFold = foldText(item.category);
    if (catFold === term)
        best = Math.max(best, ITEM_CATEGORY_EXACT);
    else if (catFold.startsWith(term))
        best = Math.max(best, ITEM_CATEGORY_PREFIX);
    for (const value of Object.values(item.metadata)) {
        if (typeof value === "string" && foldText(value).includes(term))
            best = Math.max(best, ITEM_METADATA);
    }
    return best;
}
/**
 * Does the item mention any folded term in name/barcode/category/metadata?
 * The tolerant boolean filter of the list/search paths.
 */
export function itemMatchesTerms(item, terms) {
    return terms.some((term) => itemTermScore(item, term) > 0);
}
/**
 * The single item search scorer. Sums the strongest field score per term;
 * matchedTerms counts how many terms hit.
 */
export function scoreItemTerms(item, terms) {
    let score = 0;
    let matchedTerms = 0;
    for (const term of terms) {
        const best = itemTermScore(item, term);
        if (best > 0) {
            score += best;
            matchedTerms += 1;
        }
    }
    return { score, matchedTerms };
}
//# sourceMappingURL=search-scoring.js.map